import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { Dialog as MuiDialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

import type { MakeStyleHookType, Theme } from '@admin/shared/themes/default';

import { Button } from '@admin/shared/components/buttons/button/button';
import { Headline } from '@admin/shared/components/typography/headline';
import { CloseIcon } from '@admin/shared/components/icons/custom/close';
import { IconButton } from '@admin/shared/components/buttons/button/icon-button';

// create styles for dialog
export const useDialogStyles: MakeStyleHookType = makeStyles(
  (theme: Theme) => {
    return {
      paper: {
        padding: 24,
        width: 'auto',

        '&$small': {
          maxWidth: 348,
        },
        '&$large': {
          maxWidth: 550,
        },
      },

      dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignContent: 'center',
        padding: '0',
        marginBottom: 24,

        '& button': {
          color: theme.palette.icon.primary,
        },
      },

      dialogContent: {
        height: '100%',
        padding: 0,
        margin: 0,
        overflowX: 'hidden',
        overflowY: 'auto',

        '&:first-child': {
          padding: 0,
        },
      },

      dialogActions: {
        marginTop: 24,
        padding: 0,
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'center',
      },

      dialogAction: {
        flex: '0 auto',
        width: 'auto',
        paddingLeft: 28,
        paddingRight: 28,

        '& + &': {
          marginLeft: 24,
        },

        '&:nth-child(3)': {
          marginLeft: 24,
        },
      },

      icon: {
        color: theme.palette.icon.primary,
        flex: '0 0 56px',
        width: 56,
      },

      headline: {
        fontSize: 14,
        lineHeight: '23px',
        fontWeight: 500,
        margin: '24px 0 16px 0',
        textAlign: 'center',
      },

      text: {
        fontSize: 14,
        lineHeight: '23px',
        color: theme.palette.grey['600'],
        textAlign: 'center',
      },

      noPadding: {
        padding: 0,
      },

      small: {},
      large: {},
      auto: {},
    };
  },
  {
    name: 'OMQDialog',
  },
);

export enum DialogSizes {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
  AUTO = 'AUTO',
}

/**
 * Type of component properties
 */
export type DialogProps = {
  title?: string;
  children: JSX.Element;
  size?: DialogSizes;
  actions?: Array<{
    label: string;
    action?: () => void | Promise<void>;
    color?: string;
    autoFocus?: boolean;
  }>;
  classes?: Record<string, string>;
  open?: boolean;
  noPadding?: boolean;
  onClose?: () => void;
};

/**
 * Dialog/Alert component.
 *
 * Shows title/description in a modal window.
 * Provides action buttons for the user.
 *
 * Based on the material UI Dialog component
 * @see https://material-ui.com/components/dialogs/
 *
 * @param {string} title - title of dialog
 * @param {string} content - description text
 * @param {Array} actions - array of actions
 * @param {Function} onClose - close handler
 * @param {{}} classes - additional classes
 *
 * @returns {JSX.Element}
 */
export const Dialog = ({
  title,
  children,
  actions,
  open = true,
  noPadding = false,
  onClose,
  classes = {},
  size = DialogSizes.AUTO,
}: DialogProps): JSX.Element => {
  const styles = useDialogStyles();
  const { t } = useTranslation();

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{
        paper: clsx(styles.paper, styles[size.toLowerCase()], classes.paper, {
          [styles.noPadding]: noPadding,
        }),
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      {title != null && (
        <DialogTitle
          disableTypography={true}
          classes={{
            root: styles.dialogTitle,
          }}>
          <Headline>
            <>{title}</>
          </Headline>
          {onClose != null && (
            <IconButton tooltip={t('common:actions.close')} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}

      <DialogContent
        classes={{
          root: clsx(styles.dialogContent, classes.dialogContent),
        }}>
        {children}
      </DialogContent>

      {actions != null && (
        <DialogActions
          disableSpacing={true}
          classes={{
            root: clsx(styles.dialogActions, classes.dialogActions),
          }}>
          {actions.map((button) => {
            const { label, color, action, autoFocus } = button;

            return (
              <Button
                key={label}
                classes={{
                  root: clsx(styles.dialogAction, classes.dialogAction),
                }}
                onClick={() => {
                  if (onClose != null) {
                    onClose();
                  }

                  if (action != null) {
                    action();
                  }
                }}
                color={color == null ? 'default' : color}
                autoFocus={autoFocus == null ? false : autoFocus}>
                <>{label}</>
              </Button>
            );
          })}
        </DialogActions>
      )}
    </MuiDialog>
  );
};
